import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatTableDataSource } from "@angular/material";
import { BehaviorSubject } from "rxjs";

import { OrderItem, OrderTracking } from "../../../core/e-commerce/_models/order";
import { CheckoutModel } from "../../../core/e-commerce/_services/orders.service";
import { Campaign } from "../../../core/models/campaign.model";
import { ClaimedProductDto } from "../../../model/club-gigot/user-points/claimed-product.dto";
import { MoneyPipe } from "../../../model/pipe/money.pipe";
import { CountryService } from "../../../service/country.service";
import { CatalogService } from "../../../service/club-gigot/catalog.service";
import { BarcodeComponent } from "../barcode/barcode.component";
import { CampaignService } from "../../../core/services/campaign.service";
import { EnvironmentService } from "../../../service/environment.service";
import { Country } from "../../../model/country.enum";
import { OrderInvoiceTypeLabel } from "../../../persistence/dto/order-invoice-type-label.enum";
import { OrderCheckout } from "../../../persistence/dto/order-checkout.model";
import { AuthenticationService } from "../../../service/authentication.service";
import { BackendPermission } from "../../../model/backend-permission.enum";

export interface DialogData {
	checkoutModel: CheckoutModel;
	currentCampaign: Campaign;
}

@Component({
	selector: "kt-view-order",
	templateUrl: "./view-order.component.html",
	styleUrls: ["./view-order.component.scss"],
	providers: [MoneyPipe]
})
export class ViewOrderComponent implements OnInit {

	private orderTracking: OrderTracking[];

	public displayedColumns: string[] = [
		"productCode", "image", "productName", "brochurePrice",
		"priceToPay", "quantity", "profit", "totalPrice"
	];

	public displayedPointsColumns: string[] = [
		"productCode", "image", "name", "quantity", "gigot-points-logo"
	];

	public orderItems = new MatTableDataSource([]);
	public orderItemsCortados = new MatTableDataSource([]);
	public orderItemsisPoints = new MatTableDataSource([]);

	public tables: any[] = [{
		dataSource: new MatTableDataSource([]),
		emptyMessage: "No hay productos en este pedido.",
		title: "Productos pedidos"
	}, {
		dataSource: new MatTableDataSource([]),
		emptyMessage: "No hay productos cortados en este pedido.",
		title: "Productos cortados"
	}];

	public tablesPoint: any[] = [{
		dataSource: new MatTableDataSource([]),
		emptyMessage: "No hay productos canjeados en este pedido.",
		title: "Productos canjeados"
	}];

	public loading$ : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    public claimsDataSource : ClaimedProductDto[];
    public claimsColumns : string[] = ["image", "name", "quantity", "gigot-points-logo"]

	public country = Country;
	public checkout = new OrderCheckout();

	constructor(
		private countryService : CountryService,
		private moneyPipe: MoneyPipe,
		public dialogRef: MatDialogRef<ViewOrderComponent>,
		@Inject(MAT_DIALOG_DATA) public data: DialogData, private dialog: MatDialog,
		private rewardsService : CatalogService,
		private campaignService: CampaignService,
		public environmentService : EnvironmentService,
        private authService: AuthenticationService) {
		this.orderItems = new MatTableDataSource(data.checkoutModel.order.orderItems
			.filter((item: any) => item.orderItemStatus !== "NoStock" && item.isPoints !== true));
		this.tables[0].dataSource = this.orderItems;
		const filter = data.checkoutModel.order.orderItems
			.filter((item: any) => item.orderItemStatus === "NoStock");
		this.orderItemsCortados = new MatTableDataSource(filter);
		this.tables[1].dataSource = this.orderItemsCortados;
		const filterPoints = data.checkoutModel.order.orderItems
			.filter((item: any) => item.isPoints === true);
		this.orderItemsisPoints = new MatTableDataSource(filterPoints);
		this.tablesPoint[0].dataSource = this.orderItemsisPoints;
		this.orderTracking = data.checkoutModel.order.orderTracking;
	}

	public ngOnInit(): void {
        if (!this.authService.hasPermission(BackendPermission.PointsProgramClosed)) {
            this.getClaimedRewards();
        }
		this.loadCheckout();
	}

	/**
	 * @description
	 *	Computa el precio de folleto.
	 */
	public brochurePrice(item: OrderItem): string {
		if (item.isGift) {
			return "¡Regalo!";
		}
		else {
			if (0 < item.promotionPrice) {
				if (item.promotionPriceQuantity === item.quantity) {
					return `<div><s>${this.toCurrency(item.price)}</s></div>${this.toCurrency(item.promotionPrice)}`;
				}
				else {
					return `<div>${item.quantity - item.promotionPriceQuantity} de ${this.toCurrency(item.price)}</div>`
						+ `${item.promotionPriceQuantity} de ${this.toCurrency(item.promotionPrice)}`;
				}
			}
			else {
				return `${this.toCurrency(item.price)}`;
			}
		}
	}

	public getTrackingIcon(orderStatus: string): string {
		const tracking = this.orderTracking.find(value => value.orderStatus === orderStatus);
		if (tracking) {
			return "done";
		}
		else {
			return "query_builder";
		}
	}

	public getTrackingTimestamp(orderStatus: string): string {
		const tracking = this.orderTracking.find(value => value.orderStatus === orderStatus);
		if (tracking) {
			return tracking.timestamp;
		}
		else {
			return "-";
		}
	}

	public onCancelClick(): void {
		this.dialogRef.close();
	}

	/**
	 * @description
	 *	Abre un popup para visualizar el codigo de barras para hacer el pago en Pago Facil.
	 *  Permite tambien descargar ese codigo de barras.
	 */
	public onViewBarcode(): void {
		const order = this.data.checkoutModel.order;
		this.dialog.open(BarcodeComponent, {
			width: '800px',
			data: {
				barcodeNumber: this.data.checkoutModel.barCode,
				resellerName: `${order.applicationUser.firstName} ${order.applicationUser.lastName} (${order.applicationUser.userName})`,
				balance: this.data.checkoutModel.resellerStamentBalance,
				orderId: order.id,
				title: 'Código de Barras Pago Fácil'
			}
		});
	}

	/**
	 * @description
	 *	Computa el precio a pagar.
	 */
	public priceToPay(item: OrderItem): string {
		if (item.isGift) {
			return "¡Regalo!";
		}
		else {
			if (0 < item.promotionPrice) {
				if (item.promotionPriceQuantity === item.quantity) {
					return `${this.toCurrency(item.finalDiscountedPrice)}`;
				}
				else {
					return `<div>${item.quantity - item.promotionPriceQuantity} de ${this.toCurrency(item.finalPrice)}</div>`
						+ `${item.promotionPriceQuantity} de ${this.toCurrency(item.finalDiscountedPrice)}`;
				}
			}
			else {
				const finalPrice = `<div>${this.toCurrency(item.finalPrice)}</div>`;
				if (1 < item.totalPaymentCycle && !item.isReturn) {
					return `Cuota&nbsp;${item.paymentCycle}/${item.totalPaymentCycle}: ${finalPrice}`;
				}
				else {
					return finalPrice;
				}
			}
		}
	}

	/**
	 * @description
	 *	Computa la ganancia.
	 */
	public profit(item: OrderItem): string {
		if (item.isGift) {
			return "¡Regalo!";
		}
		else {
			if (0 < item.promotionPrice) {
				const baseProfit = (item.promotionPrice - item.finalDiscountedPrice * item.totalPaymentCycle) * item.promotionPriceQuantity;
				if (item.promotionPriceQuantity === item.quantity) {
					if (1 < item.paymentCycle) {
						return `${this.toCurrency(0)}`;
					}
					else {
						return `${this.toCurrency(baseProfit)}`;
					}
				}
				else {
					if (1 < item.paymentCycle) {
						return `${this.toCurrency(0)}`;
					}
					else {
						const profit = baseProfit + (item.price - item.finalPrice * item.totalPaymentCycle)
							* (item.quantity - item.promotionPriceQuantity);
						return `${this.toCurrency(profit)}`;
					}
				}
			}
			else {
				if (1 < item.paymentCycle) {
					return `${this.toCurrency(0)}`;
				}
				else {
					const profit = item.quantity * (item.price - item.finalPrice * item.totalPaymentCycle);
					return `${this.toCurrency(profit)}`;
				}
			}
		}
	}

	public translateStatus(orderStatus): string {
		switch (orderStatus) {
			case 'PendingDebt':
				return 'Pendiente por deuda';
			case 'ReadyForInvoice':
				return 'Aprobado';
			default:
				return 'Aprobado';
		}
	}

	public title(): string {
		const order = this.data.checkoutModel.order;
		const resellerName = `${order.applicationUser.firstName} ${order.applicationUser.lastName}`;
		return `Pedido Nº ${order.id} - Revendedora "${order.applicationUser.userName}" (${resellerName})`;
	}

	/**
	 * @description
	 *	Computa el total a pagar para productos con y sin stock.
	 */
	public totalPrice(item: OrderItem): string {
		if (item.isGift) {
			return "¡Regalo!";
		}
		else {
			if (0 < item.promotionPrice) {
				if (item.promotionPriceQuantity === item.quantity) {
					return `${this.toCurrency(item.finalDiscountedPrice * item.quantity)}`;
				}
				else {
					const totalPrice = item.finalDiscountedPrice * item.promotionPriceQuantity
						+ item.finalPrice * (item.quantity - item.promotionPriceQuantity);
					return `${this.toCurrency(totalPrice)}`;
				}
			}
			else {
				const totalPrice = item.finalPrice * item.quantity;
				return `${this.toCurrency(totalPrice)}`;
			}
		}
	}

	private toCurrency(value: number): string {
		return this.moneyPipe.transform(value);
	}

	public grandfatherStructureName(): string {
        return this.countryService.grandfatherStructureName();
    }

	private getClaimedRewards(){
        this.campaignService.getCampaigns().subscribe({
            next: (campaigns: any) => {
                let sequence = (campaigns.filter(c => c.id == this.data.checkoutModel.order.campaignId)[0]).sequence;
				this.rewardsService.getApplicableClaimedRewards(this.data.checkoutModel.order.applicationUser.userName.toString(), sequence)
				.subscribe({
					next : (response : ClaimedProductDto[]) => {
						this.claimsDataSource = response.filter(c => c.orderId === null)
						this.loading$.next(false)
					},
					error : (error : Error) => console.log(error)
				})
            },
			error : (error : Error) => console.log(error)
        })
    }

	private loadCheckout(){
		this.checkout.invoiceTypeLabel= this.data.checkoutModel.order.invoiceTypeLabel;
		this.checkout.shippingAmount= this.data.checkoutModel.order.shippingAmount;
		this.checkout.excemptTaxAmount=this.data.checkoutModel.order.excemptTaxAmount;
		this.checkout.ivaAmount= this.data.checkoutModel.order.vatAmount;
		this.checkout.iibbPercentage=this.data.checkoutModel.order.grossIncomePercentageAmount;
		this.checkout.iibbPercentageAmount=this.data.checkoutModel.order.grossIncomeTaxAmount;
		this.checkout.otherTaxes=this.data.checkoutModel.otherTaxes;
		this.checkout.taxAndProfitAmount=this.data.checkoutModel.taxAndProfitAmount;
		this.checkout.subTotalAmount=this.data.checkoutModel.subTotalAmount;
		this.checkout.totalAmount=this.data.checkoutModel.totalAmount;

		switch (this.data.checkoutModel.order.invoiceTypeLabel) {
			case OrderInvoiceTypeLabel.NC: {
				//this.checkout.ivaAmount = this.data.checkoutModel.order.ivaNoCatTaxAmount;
				//this.checkout.otherTaxes = 0;
				break;
			}
			case OrderInvoiceTypeLabel.CF: {
				// if (this.data.checkoutModel.order.grossIncomePercentageAmount == 0)
				// 	this.checkout.iibbPercentageAmount = 0;
				// this.checkout.otherTaxes=this.data.checkoutModel.order.grossIncomeTaxAmount;
				break;
			}
			case OrderInvoiceTypeLabel.RI: {
				//this.checkout.ivaAmount=0;
				//this.checkout.otherTaxes=0;
				break;
			}
			case OrderInvoiceTypeLabel.MT: {
				//this.checkout.ivaAmount=0;
				//this.checkout.otherTaxes=0;
				break;
			}
			case OrderInvoiceTypeLabel.EX: {
				//this.checkout.ivaAmount=0;
				//this.checkout.otherTaxes=0;
				break;
			}
			default: {
				this.checkout = OrderCheckout.empty();
				break;
			}
		}
    }
}
